import {
  Search,
  DollarSign,
  Building2,
  CreditCard,
  Users,
  Package,
  TrendingUp,
  TrendingDown,
  Briefcase,
  Home,
  Menu,
  X,
  TicketCheck,
  Landmark,
  HandCoins,
} from "lucide-react";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";
import checkIfUserHasAccess from "./UserCheckAccess";
import { Refresh } from "@mui/icons-material";

const VirtualList = ({
  items,
  itemHeight = 48,
  containerHeight = 390,
  onItemClick,
  selectedIndex,
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);

  // Column width configuration (in pixels)
  const columnWidths = {
    account: 150,
    description: 350,
    phone: 150,
    balance: 120,
  };

  const totalWidth = Object.values(columnWidths).reduce(
    (sum, width) => sum + width,
    0,
  );

  // Calculate visible range
  const visibleRange = useMemo(() => {
    const start = Math.floor(scrollTop / itemHeight);
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    const end = Math.min(start + visibleCount + 1, items.length);
    return { start, end };
  }, [scrollTop, itemHeight, containerHeight, items.length]);

  // Handle scroll
  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  // Scroll to selected item
  useEffect(() => {
    if (selectedIndex >= 0 && containerRef.current) {
      const itemTop = selectedIndex * itemHeight;
      const itemBottom = itemTop + itemHeight;
      const containerTop = containerRef.current.scrollTop;
      const containerBottom = containerTop + containerHeight;
      if (itemTop < containerTop || itemBottom > containerBottom) {
        containerRef.current.scrollTo({
          top: itemTop,
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex, itemHeight, containerHeight]);

  useEffect(() => {
    // Reset scroll position when items change
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [items]);

  const formatBalance = (balance) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(balance);

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto w-full"
      style={{ height: containerHeight }}
      onScroll={handleScroll}
    >
      <div
        style={{
          height: items.length * itemHeight,
          position: "relative",
          minWidth: totalWidth,
        }}
      >
        {items
          .slice(visibleRange.start, visibleRange.end)
          .map((item, index) => {
            const actualIndex = visibleRange.start + index;
            return (
              <div
                key={`${item.ACCOUNT}-${actualIndex}`}
                className={`absolute w-full cursor-pointer border-b border-gray-100 ${actualIndex === selectedIndex
                  ? "bg-blue-100"
                  : "hover:bg-gray-100"
                  }`}
                style={{
                  top: actualIndex * itemHeight,
                  height: itemHeight,
                  display: "flex",
                  alignItems: "center",
                  minWidth: totalWidth,
                }}
                onClick={() => onItemClick(item, actualIndex)}
              >
                {/* Account Column */}
                <div
                  className="px-3 py-3 text-sm font-medium text-gray-900 truncate"
                  style={{
                    width: columnWidths.account,
                    minWidth: columnWidths.account,
                  }}
                >
                  {item.ACCOUNT}
                </div>

                {/* Description Column */}
                <div
                  title={item.DESCRIPTION}
                  className="px-3 py-3 text-sm text-gray-700 truncate"
                  style={{
                    width: columnWidths.description,
                    minWidth: columnWidths.description,
                  }}
                >
                  {item.DESCRIPTION}
                </div>

                {/* Phone Column */}
                <div
                  className="px-3 py-3 text-sm text-gray-700 truncate"
                  style={{
                    width: columnWidths.phone,
                    minWidth: columnWidths.phone,
                  }}
                >
                  {item.Contact_No}
                </div>

                {/* Balance Column */}
                {checkIfUserHasAccess("CT") && (
                  <div
                    className={`px-3 py-3 text-sm text-right font-medium ${item.BALANCE > 0
                      ? "text-green-600"
                      : item.BALANCE < 0
                        ? "text-red-600"
                        : "text-gray-700"
                      }`}
                    style={{
                      width: columnWidths.balance,
                      minWidth: columnWidths.balance,
                    }}
                  >
                    {formatBalance(item.BALANCE)}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

const CreditCustomerList = ({
  showSidebar = true,
  handleCustomerSelect,
  defaultType = "All",
  companyCode,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState(defaultType || "All");
  const [sidebarVisible, setSidebarVisible] = useState(showSidebar);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const [tableHeight, setTableHeight] = useState(400); // default fallback
  const containerRef = useRef(null);

  const COMPANY_CODE = companyCode || localStorage?.getItem("companyCode") || "-";
  const DEPARTMENT_NO =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0].API_CONFIG || "https://cubixweberp.com:213";

  // Column width configuration (must match VirtualList)
  const columnWidths = {
    account: 150,
    description: 350,
    phone: 150,
    balance: 120,
  };

  const totalWidth = Object.values(columnWidths).reduce(
    (sum, width) => sum + width,
    0,
  );

  const types = [
    { name: "All", icon: Home },
    { name: "Cash", icon: DollarSign },
    { name: "Bank", icon: Landmark },
    { name: "PDC", icon: TicketCheck },
    { name: "Customers", icon: Users },
    { name: "Suppliers", icon: Package },
    { name: "Asset", icon: Briefcase },
    { name: "Liabilities", icon: CreditCard },
    { name: "Income", icon: TrendingUp },
    { name: "Expenses", icon: TrendingDown },
    { name: "InExpenses", icon: HandCoins },
    { name: "InterCompany", icon: Building2, disable: false },
  ];

  const fetchCustomerList = async (
    type = "All",
    salesperson = "-",
    freetext = "-",
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/${type}/${salesperson}/${freetext}`;

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: "GET",
        signal: abortControllerRef.current.signal,
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data || []);
      } else {
        setCustomers([]);
        console.error("API response not ok:", response.status);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error loading data:", err);
        setCustomers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const { selectedIndex, setSelectedIndex } = useKeyboardNavigation({
    list: customers,
    onEnter: handleCustomerSelect,
    isActive: !loading && customers.length > 0,
    enableScroll: false,
  });

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const freetext = searchTerm.trim() === "" ? "-" : searchTerm.trim();
      fetchCustomerList(selectedType, "-", freetext);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [selectedType, searchTerm, COMPANY_CODE, DEPARTMENT_NO]);

  // Keyboard shortcuts for sidebar navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + K: Toggle sidebar
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSidebarVisible((prev) => !prev);
        return;
      }

      // Ctrl/Cmd + Number: Quick jump to account type
      if ((e.ctrlKey || e.metaKey) && e.key >= "1" && e.key <= "9") {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (index < types.length && !types[index].disable) {
          handleTypeChange(types[index].name);
        }
        return;
      }

      // Tab/Shift+Tab: Cycle through account types
      if (e.key === "Tab" && !e.altKey) {
        // Only intercept if not in an input field
        if (document.activeElement?.tagName === "INPUT") return;

        e.preventDefault();
        const currentIndex = types.findIndex((t) => t.name === selectedType);
        let nextIndex;

        if (e.shiftKey) {
          // Shift+Tab: go backwards
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = types.length - 1;
        } else {
          // Tab: go forwards
          nextIndex = currentIndex + 1;
          if (nextIndex >= types.length) nextIndex = 0;
        }

        // Skip disabled types
        while (types[nextIndex]?.disable) {
          nextIndex = e.shiftKey ? nextIndex - 1 : nextIndex + 1;
          if (nextIndex < 0) nextIndex = types.length - 1;
          if (nextIndex >= types.length) nextIndex = 0;
        }

        handleTypeChange(types[nextIndex].name);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedType, types]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (defaultType && defaultType !== selectedType) {
      setSelectedType(defaultType);
    }
  }, [defaultType]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarVisible(window.innerWidth >= 768 ? showSidebar : false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showSidebar]);

  useEffect(() => {
    const calculateHeight = () => {
      if (!containerRef.current) return;

      const totalHeight = window.innerHeight;
      const rect = containerRef.current.getBoundingClientRect();

      // Distance from container top to bottom of viewport
      const availableHeight = totalHeight - rect.top - 20; // 20px for bottom padding
      setTableHeight(availableHeight);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  const handleItemClick = useCallback(
    (item, index) => {
      setSelectedIndex(index);
      handleCustomerSelect?.(item);
    },
    [setSelectedIndex, handleCustomerSelect],
  );

  const handleTypeChange = useCallback((newType) => {
    setSelectedType(newType);
    setSearchTerm("");
  }, []);
  const refreshCustomers = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/api/CRMAccRefresh/${COMPANY_CODE}`, {
        method: "GET",
      });

      // After refresh, reload the list
      const freetext = searchTerm.trim() === "" ? "-" : searchTerm.trim();
      fetchCustomerList(selectedType, "-", freetext);
    } catch (error) {
      console.error("Customer refresh failed", error);
    }
  }, [COMPANY_CODE, selectedType, searchTerm]);

  return (
    <div className="bg-gray-50 h-screen flex flex-col">
      <div className="flex flex-1 overflow-hidden">
        {sidebarVisible && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarVisible(false)}
          />
        )}

        {/* Sidebar - Independent Scroll */}
        {sidebarVisible && (
          <div
            className={`${sidebarVisible ? "translate-x-0" : "-translate-x-full"
              } fixed md:static top-0 left-0 z-50 md:z-auto w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out h-full flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sidebar Header - Fixed */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Types
                </h3>
                <button
                  onClick={() => setSidebarVisible(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Close sidebar (Ctrl+K)"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Sidebar Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <nav className="space-y-1">
                  {types.map(({ name, icon: Icon, disable = false }, index) => {
                    const isSelected = selectedType === name;
                    return (
                      <button
                        key={name}
                        onClick={() => {
                          if (!disable) {
                            handleTypeChange(name);
                            if (window.innerWidth < 768) {
                              setSidebarVisible(false);
                            }
                          }
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors group
                        ${disable ? "opacity-60 cursor-not-allowed" : ""}
                        ${isSelected
                            ? "bg-blue-100 text-blue-700 border border-blue-200"
                            : "text-gray-700 hover:bg-gray-100"
                          }`}
                        disabled={disable}
                        title={
                          index < 9 && !disable
                            ? `Shortcut: Ctrl+${index + 1}`
                            : ""
                        }
                      >
                        <div className="flex items-center">
                          <Icon className="w-4 h-4 mr-3" />
                          {name}
                        </div>
                        {index < 9 && !disable && (
                          <kbd
                            className={`px-2 py-0.5 text-xs font-mono rounded border transition-all lg:block hidden
                            ${isSelected
                                ? "bg-blue-200 border-blue-300 text-blue-800"
                                : "bg-gray-100 border-gray-300 text-gray-600 group-hover:bg-gray-200 group-hover:border-gray-400"
                              }`}
                          >
                            ⌘{index + 1}
                          </kbd>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Keyboard shortcuts help - Fixed at bottom */}
            { /* <div className="p-4 border-t border-gray-200 lg:flex flex-col hidden flex-shrink-0 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center">
                <span>Keyboard Shortcuts</span>
              </p>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Toggle sidebar</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono shadow-sm">
                    Ctrl+K
                  </kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Jump to type</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono shadow-sm">
                    Ctrl+1-9
                  </kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Next type</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono shadow-sm">
                    Tab
                  </kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Previous type</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono shadow-sm">
                    Shift+Tab
                  </kbd>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-600">Navigate list</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono shadow-sm">
                    ↑↓
                  </kbd>
                </div>
              </div>
            </div>*/}
          </div>
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div ref={containerRef} className="lg:p-6 p-2">
              <div className="mb-6 flex items-center lg:space-x-4 ">
                {!sidebarVisible && (
                  <button
                    onClick={() => setSidebarVisible(true)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg relative group"
                    title="Open sidebar (Ctrl+K)"
                  >
                    <Menu className="w-5 h-5" />
                    <span className="lg:flex hidden absolute -bottom-1 -right-1 px-1 py-0.5 bg-blue-500 text-white text-[10px] font-mono rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      ⌘K
                    </span>
                  </button>
                )}
                <div className="relative lg:flex-1 ">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search accounts..."
                    value={searchTerm}
                    ref={inputRef}
                    autoFocus
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white shadow-sm"
                  />

                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        inputRef.current?.focus();
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={refreshCustomers}
                  className="ml-2 text-white bg-red-500 hover:text-gray-600 focus:outline-none text-sm flex items-center space-x-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  title="Refresh customer list"
                >
                  <Refresh className="w-5 h-5" />
                  Refresh
                </button>
              </div>

              <div className="mb-4 flex items-center justify-between">
                {loading ? (
                  <p className="text-sm text-gray-600">Loading...</p>
                ) : (
                  <p className="text-sm text-gray-600">
                    Showing {customers?.length} accounts{" "}
                    {selectedType !== "All" && `in ${selectedType}`}
                    {searchTerm && ` matching "${searchTerm}"`}
                  </p>
                )}
                {!sidebarVisible && selectedType !== "All" && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Filter:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {selectedType}
                    </span>
                    <button
                      onClick={() => handleTypeChange("All")}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Virtual Table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-200 border-b border-gray-200 sticky top-0 z-10 overflow-x-auto">
                  <div className="flex" style={{ minWidth: totalWidth }}>
                    <div
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300"
                      style={{
                        width: columnWidths.account,
                        minWidth: columnWidths.account,
                      }}
                    >
                      Account
                    </div>
                    <div
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300"
                      style={{
                        width: columnWidths.description,
                        minWidth: columnWidths.description,
                      }}
                    >
                      Description
                    </div>
                    <div
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300"
                      style={{
                        width: columnWidths.phone,
                        minWidth: columnWidths.phone,
                      }}
                    >
                      Phone
                    </div>
                    {checkIfUserHasAccess("CT") && (
                      <div
                        className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                        style={{
                          width: columnWidths.balance,
                          minWidth: columnWidths.balance,
                        }}
                      >
                        Balance
                      </div>
                    )}
                  </div>
                </div>

                {/* Virtual List Content */}
                {loading ? (
                  <div className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mb-3" />
                      <p className="text-sm text-gray-500">
                        Loading accounts...
                      </p>
                    </div>
                  </div>
                ) : customers?.length === 0 ? (
                  <div className="py-12 text-center">
                    <Search className="w-12 h-12 text-gray-300 mb-3 mx-auto" />
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No accounts found
                    </p>
                    <p className="text-sm text-gray-500 ">
                      Try a different filter or search
                    </p>
                  </div>
                ) : (
                  <VirtualList
                    items={customers}
                    key={selectedType}
                    itemHeight={48}
                    containerHeight={tableHeight - 86}
                    onItemClick={handleItemClick}
                    selectedIndex={selectedIndex}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCustomerList;
